import { useScopedTranslation } from 'application/localization/useScopedTranslation';
import { Field } from 'components/uikit/Field';
import { FormError } from 'components/uikit/FormError';
import { Input } from 'components/uikit/inputs/Input';
import { Button, ButtonColor } from 'components/uikit/buttons/Button';
import { CustomModal } from 'components/uikit/modal/CustomModal';
import { Loading } from 'components/uikit/suspense/Loading';
import { requiredRule } from 'helpers/form/react-hook-form-helper';
import { useAdvancedForm } from 'helpers/form/useAdvancedForm';
import { createGuid } from 'helpers/key-helpers';

import styles from './AddFolderModal.module.scss';
import { useEffect } from 'react';
import { nameEndRegex, nameStartRegex } from '../document-list-helper';

type AddFolderProps = {
  createFolderNode: (name: string, id: string) => Promise<void>;
  onClose: () => void;
  isOpen: boolean;
};

export const AddFolderModal: React.FC<AddFolderProps> = (props) => {
  const i18n = useScopedTranslation(
    'Page.Projects.ProjectDetailsPage.FoldersTree',
  );
  const buttonTitle = useScopedTranslation('Button.Title');

  const form = useAdvancedForm<{ name: string }>(async (data) => {
    const id = createGuid();
    await props.createFolderNode(data.name, id);
    form.reset();
    props.onClose();
  });

  const onModalClose = () => {
    form.reset();
    form.clearErrors();
    props.onClose();
  };
  useEffect(() => {
    if (props.isOpen) {
      setTimeout(() => {
        form.setFocus('name');
      });
    }
  }, [form, props.isOpen]);

  return (
    <CustomModal
      onClose={props.onClose}
      isOpen={props.isOpen}
      title={i18n.t('create_folder')}
      isBlocking={true}
    >
      <Loading loading={form.formState.isSubmitting}>
        <form onSubmit={form.handleSubmitDefault}>
          <Field title={i18n.t('name')}>
            <Input
              {...form.register('name', {
                ...requiredRule(),
                /*Folder must have correct name to have possibility of export to SharePoint*/
                validate: {
                  hasSpecialCharStart: (value) =>
                    nameStartRegex.test(value) ||
                    i18n.t('TreeItem.unacceptable_characters'),
                  hasSpecialCharEnd: (value) =>
                    nameEndRegex.test(value) ||
                    i18n.t('TreeItem.unacceptable_characters_end'),
                },
              })}
              errorText={form.formState.errors.name?.message}
            />
          </Field>
          <FormError>
            {form.formState.errors.name?.type === 'incorrectNameStart' &&
              i18n.t('TreeItem.unacceptable_characters')}
          </FormError>
          <FormError>{form.overallError}</FormError>
          <div className={styles.footer}>
            <Button
              className={styles.button}
              color={ButtonColor.Secondary}
              title={buttonTitle.t('cancel')}
              onClick={async () => {
                onModalClose();
              }}
            />
            <Button
              className={styles.button}
              color={ButtonColor.Default}
              type={'submit'}
              title={buttonTitle.t('save')}
            />
          </div>
        </form>
      </Loading>
    </CustomModal>
  );
};
